// web/src/lib/storage.ts
// Upload helper: writes the user's source photo to Firebase Storage under
// users/{uid}/uploads/{roastId}/source.jpg — the path the Storage rules guard.
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";

/**
 * Upload the source photo to users/{uid}/uploads/{roastId}/source.jpg.
 *
 * Matches storage.rules: owner-write, image/* contentType, < 25 MB.
 * Returns:
 *   imagePath — Storage object path passed to generateRoast callable.
 *   imageUrl  — Signed download URL used for preview in the UI.
 */
export async function uploadSourcePhoto(
  uid: string,
  roastId: string,
  file: File,
): Promise<{ imagePath: string; imageUrl: string }> {
  const imagePath = `users/${uid}/uploads/${roastId}/source.jpg`;
  const objectRef = ref(storage, imagePath);
  await uploadBytes(objectRef, file, {
    contentType: file.type || "image/jpeg",
  });
  const imageUrl = await getDownloadURL(objectRef);
  return { imagePath, imageUrl };
}

/**
 * Upload the source PDF to users/{uid}/uploads/{roastId}/source.pdf — the path
 * comprehendDeck downloads server-side (and the uid-scope its guard enforces).
 * Matches storage.rules: owner-write, application/pdf, < 50 MB. Only the PDF is
 * staged at generate; rendered rasters are uploaded to roasts/{id}/ at save (D4).
 */
export async function uploadSourcePdf(
  uid: string,
  roastId: string,
  file: Blob,
): Promise<{ pdfPath: string }> {
  const pdfPath = `users/${uid}/uploads/${roastId}/source.pdf`;
  const objectRef = ref(storage, pdfPath);
  await uploadBytes(objectRef, file, { contentType: "application/pdf" });
  return { pdfPath };
}
